import { productEvent } from "@shared/analytics/productEvents";
import {
    useProductJourney,
    useProductObservation
} from "@shared/analytics/useProductJourney";
import { FEATURE_FLAGS } from "@solomon-apps/config";
import { formatCurrency } from "@solomon-apps/shared";
import type { StatBandStat } from "@solomon-labs/design/components/layout";
import {
    Heading,
    Skeleton,
    TabsMenu,
    TabTrigger
} from "@solomon-labs/design/components/ui";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
    useProtocolData,
    useReservesData,
    useUsdvSupply,
    useYieldData
} from "@/hooks/stats";
import { useOffApiTreasuryBalancesQuery } from "@/hooks/stats/useOffApiTreasuryBalances";
import { platformStatsQuery } from "@/hooks/yaas/platformStatsQuery";
import { StatsHeader } from "@/pages/Stats/components/StatsHeader";
import { YieldContent } from "@/pages/Stats/components/YieldContent";

import { ReserveComposition } from "./components/ReserveComposition";
import { ReservesSummary } from "./components/ReservesSummary";
import { SecurityContent } from "./components/SecurityContent";
import { TotalSupplySection } from "./components/TotalSupplySection";
import { earningSupplyDisplayAmount } from "./statsV4Data";

type StatsV4Tab = "yield" | "reserves" | "security";
type YieldRange = "7d" | "30d" | "90d" | "1y" | "all";

/*
 * The Backing Collateral figure is a fixed 100% display value, not a ratio
 * calculated from the live feed. The feed does not itemize the full reserve
 * set; `calculateBackingRatio` can be wired in once it does.
 */

/**
 * Each subtitle names what the tab actually holds, in the tab's own nouns, so
 * a reader can tell which one answers their question without opening all
 * three. No adjectives: nothing here is "comprehensive" or "full", and a
 * transparency page that oversells is the one nobody believes.
 */
const TABS: ReadonlyArray<{
    key: StatsV4Tab;
    title: string;
    subtitle: string;
}> = [
    {
        key: "reserves",
        title: "Reserves",
        subtitle: "Backing and Supply"
    },
    {
        key: "yield",
        title: "Rewards",
        subtitle: "Earnings and Distributions"
    },
    {
        key: "security",
        title: "Security",
        subtitle: "Controls and Resources"
    }
];

/**
 * URL slug for each tab, so a tab is a shareable address: /stats/reserves,
 * /stats/rewards, /stats/security. The slug follows the label a reader sees,
 * which is why "yield" is addressed as "rewards" — the internal key predates
 * the rename and is not worth churning the data hooks over.
 */
const TAB_SLUG: Record<StatsV4Tab, string> = {
    reserves: "reserves",
    yield: "rewards",
    security: "security"
};

const SLUG_TAB: ReadonlyMap<string, StatsV4Tab> = new Map(
    (Object.keys(TAB_SLUG) as StatsV4Tab[]).map((key) => [TAB_SLUG[key], key])
);

/** Bare /stats, and any slug that is not one of the three, land here. */
const DEFAULT_TAB: StatsV4Tab = "reserves";

/**
 * A month is the default range. When it cannot plot a line, fall through to
 * the next wider window rather than showing the reader an empty one.
 */
const YIELD_RANGE_PREFERENCE: readonly YieldRange[] = [
    "30d",
    "90d",
    "1y",
    "all"
] as const;
/** Matches the floor `YieldContent` uses to decide which ranges to offer. */
const MINIMUM_RANGE_POINTS = 5;

export function LegacyStatsV4Page(): React.ReactElement {
    const navigate = useNavigate();

    // The URL owns which tab is open, so the address bar, a shared link and
    // the back button all agree. An unknown slug renders the default rather
    // than 404ing: the page is still the answer to "show me the stats".
    const { tab: tabSlug } = useParams();
    const activeTab: StatsV4Tab =
        (tabSlug === undefined ? undefined : SLUG_TAB.get(tabSlug)) ??
        DEFAULT_TAB;
    const [yieldRange, setYieldRange] = React.useState<YieldRange>("30d");

    const protocolQuery = useProtocolData();
    const reserves = useReservesData();
    const usdvSupplyQuery = useUsdvSupply();
    const yieldData = useYieldData(yieldRange, { carryForwardApy: true });

    // The counts arrive with the distributions, so the opening range can only
    // be corrected once they land. Widening happens at most once per dataset.
    const rangeCounts = yieldData.pointsByPeriod;
    React.useEffect(() => {
        if (rangeCounts[yieldRange] >= MINIMUM_RANGE_POINTS) return;
        const wider = YIELD_RANGE_PREFERENCE.find(
            (candidate) => rangeCounts[candidate] >= MINIMUM_RANGE_POINTS
        );
        if (wider && wider !== yieldRange) setYieldRange(wider);
    }, [rangeCounts, yieldRange]);
    const reserveInvestmentsQuery = useOffApiTreasuryBalancesQuery();

    const positions = protocolQuery.data?.positions ?? [];

    // Prefer the onchain mint supply: it is the figure a reader can check for
    // themselves. `protocolTvl` is the same measurement taken by the data API,
    // so it is the fallback rather than a second number.
    const supplyValue =
        usdvSupplyQuery.data?.totalSupply ?? reserves.protocolTvlNumber;

    // The off-API Paxos balance lands after the protocol feed does, and it is
    // roughly a third of the itemized total, so hold every reserve figure until
    // it arrives rather than letting the numbers jump.
    // Jachin active-account USDv balances saved by YaaS.
    const yaasStatsQuery = useQuery(platformStatsQuery);
    const earningValue = earningSupplyDisplayAmount(
        yaasStatsQuery.data?.enrolledInEarnUi
    );

    const reservesLoading =
        reserves.isLoading ||
        protocolQuery.isLoading ||
        reserveInvestmentsQuery.isPlaceholderData;
    const showReserveSummary =
        reservesLoading || reserves.reserveClasses.length > 0;
    const summaryLoading = reservesLoading || usdvSupplyQuery.isLoading;

    const summaryStats: StatBandStat[] = [
        {
            label: "Total USDv Supply",
            value: summaryLoading ? (
                <Skeleton className="h-7 w-32" />
            ) : supplyValue > 0 ? (
                formatCurrency(Math.floor(supplyValue), 0, 0)
            ) : (
                "—"
            )
        },
        {
            label: "Amount of Supply Earning",
            value: yaasStatsQuery.isLoading ? (
                <Skeleton className="h-7 w-32" />
            ) : earningValue === null ? (
                "—"
            ) : (
                formatCurrency(earningValue, 0, 0)
            )
        },
        {
            label: "Backing Collateral",
            value: "100%"
        }
    ];

    const analytics = useProductJourney("stats", "stats_page");
    useProductObservation(analytics, "viewed", { tab: TAB_SLUG[activeTab] });
    return (
        <main className="flex-1 flex flex-col items-center px-4 py-6 md:px-6 md:py-10 text-foreground-base">
            <div className="w-full max-w-5xl flex flex-col gap-10 md:gap-12">
                <section>
                    <StatsHeader />
                    <div className="h-px w-full bg-line-neutral" />

                    <TabsMenu
                        value={activeTab}
                        onValueChange={(value) => {
                            productEvent("stats:tab_selected", {
                                entry_point: "stats_page",
                                tab: TAB_SLUG[value as StatsV4Tab],
                                previous_tab: TAB_SLUG[activeTab]
                            });
                            navigate(`/stats/${TAB_SLUG[value as StatsV4Tab]}`);
                        }}
                        align="start"
                        fullWidth
                        indicatorPosition="top"
                    >
                        {/* A stacked label is not what TabTrigger's fixed
                            height and `leading-none` are shaped for, so the
                            box grows and the lines get their leading back. */}
                        {TABS.map((tab) => (
                            <TabTrigger
                                key={tab.key}
                                value={tab.key}
                                size="lg"
                                interaction="fillFromTop"
                                className="h-auto py-3 md:h-auto"
                            >
                                <span className="flex flex-col items-start gap-0.5 leading-normal">
                                    <span>{tab.title}</span>
                                    <span className="hidden md:block font-sans text-sm text-foreground-muted text-left">
                                        {tab.subtitle}
                                    </span>
                                </span>
                            </TabTrigger>
                        ))}
                    </TabsMenu>
                    <div className="h-px w-full bg-line-neutral" />

                    <div className="pt-12">
                        {activeTab === "reserves" && (
                            <div className="flex flex-col gap-8 md:gap-10">
                                <section
                                    aria-label="USDv reserves"
                                    data-motion-section
                                    style={{
                                        opacity: 0,
                                        animation:
                                            "page-fade-up 360ms ease-out 0ms forwards"
                                    }}
                                >
                                    {/* `Heading`, not `SectionHeading`: the
                                        rule under this title is drawn by the
                                        StatBand below it, and `SectionHeading`
                                        always draws one of its own, so the two
                                        would stack. `Heading`'s divider is
                                        opt-in and stays off. The padding is the
                                        clearance the design system gives an
                                        h1-scale title above a rule
                                        (`SectionHeading`'s own h1 figure), kept
                                        here because the rule still exists — it
                                        is just owned by the next element. */}
                                    {showReserveSummary && (
                                        <Heading
                                            title={
                                                <span id="usdv-supply-heading">
                                                    USDv Supply
                                                </span>
                                            }
                                            className="pb-2 md:pb-5"
                                        />
                                    )}

                                    <div className="flex flex-col gap-6">
                                        {showReserveSummary && (
                                            <ReservesSummary
                                                stats={summaryStats}
                                            />
                                        )}

                                        <ReserveComposition
                                            classes={reserves.reserveClasses}
                                            positions={positions}
                                            loading={reservesLoading}
                                        />
                                    </div>
                                </section>

                                {FEATURE_FLAGS.reserveHistoryPreview && (
                                    <TotalSupplySection
                                        history={
                                            yaasStatsQuery.data?.flowSeries ??
                                            []
                                        }
                                    />
                                )}
                            </div>
                        )}
                        {activeTab === "security" && <SecurityContent />}
                        {activeTab === "yield" && (
                            <div className="flex flex-col gap-8 md:gap-10">
                                <YieldContent
                                    apyValue={yieldData.apyValue}
                                    apyChange={yieldData.apyChange}
                                    apyPeriodLabel={yieldData.apyPeriodLabel}
                                    chartPoints={yieldData.chartPoints}
                                    chartTimestamps={yieldData.chartTimestamps}
                                    chartLabels={yieldData.chartLabels}
                                    allTimePoints={
                                        yieldData.allTimeApyHistory.length
                                    }
                                    pointsByRange={yieldData.pointsByPeriod}
                                    chartStartLabel={yieldData.chartStartLabel}
                                    chartEndLabel={yieldData.chartEndLabel}
                                    loading={yieldData.isLoading}
                                    range={yieldRange}
                                    onRangeChange={setYieldRange}
                                    headlineValueVariant="number-lg"
                                />
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </main>
    );
}
